import { toBool, lockBodyScroll, unlockBodyScroll } from "./utils.js";
import {
  CART_EVENTS,
  UI_EVENTS,
  onCartEvent,
  dispatchCartEvent,
} from "./cart-events.js";

const SELECTORS = {
  overlay: "[data-cd-overlay]",
  panel: "[data-cd-panel]",
  closeBtn: "[data-cd-close]",
  remove: "[data-cd-remove]",
};

class CartDrawer extends HTMLElement {
  #callbackFunctions = [];

  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.close = this.close.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
  }

  connectedCallback() {
    if (this.hasAttribute("hidden")) this.removeAttribute("hidden");
    this.init();
  }

  init() {
    this.#bindElements();
    this.#getDataAttributes();

    this.#bindEvents();

    if (this.showInEditor) {
      this.#setOpen(true);
      return;
    }

    this.#setOpen(false);
  }

  #bindElements() {
    this.overlay = this.querySelector(SELECTORS.overlay);
    this.panel = this.querySelector(SELECTORS.panel);
    this.closeBtn = this.querySelector(SELECTORS.closeBtn);
  }

  #getDataAttributes() {
    this.showInEditor = toBool(this.dataset.cdShowInEditor);
    this.openOnItemAdd = toBool(this.dataset.cdOpenOnItemAdd);
    this.sectionId = this.dataset.cdSectionId;
  }

  #bindEvents() {
    if (this.sectionId) {
      this.#callbackFunctions.push(
        onCartEvent(CART_EVENTS.UPDATED, () => this.refreshCartDrawer()),
      );
    }

    if (this.openOnItemAdd) {
      this.#callbackFunctions.push(
        onCartEvent(CART_EVENTS.ITEM_ADDED, () => this.open()),
      );
    }

    this.#callbackFunctions.push(
      onCartEvent(UI_EVENTS.CART_OPEN_REQUESTED, () => this.open()),
    );

    this.overlay?.addEventListener("click", this.close);
    this.closeBtn?.addEventListener("click", this.close);
    // Delegated so it survives refreshCartDrawer() replacing the inner markup.
    this.addEventListener("click", this.onRemoveClick);
  }

  #setOpen(value) {
    if (value) {
      this.classList.add("is-open");
      this.style.pointerEvents = "auto";
      document.addEventListener("keydown", this.onKeydown);
    } else {
      this.classList.remove("is-open");
      this.style.pointerEvents = "none";
      document.removeEventListener("keydown", this.onKeydown);
    }
  }

  async refreshCartDrawer() {
    const wasOpen = this.classList.contains("is-open");
    const response = await fetch(
      `${window.ThemeRoutes.root}?sections=${this.sectionId}`,
    );
    const data = await response.json();
    const html = new DOMParser().parseFromString(
      data[this.sectionId],
      "text/html",
    );

    const newCartDrawer = html.querySelector("cart-drawer");
    if (newCartDrawer) {
      this.#cleanup();
      this.innerHTML = newCartDrawer.innerHTML;
      this.init();
      if (wasOpen) this.open();
    }
  }

  open() {
    this.#setOpen(true);
    (this.closeBtn || this.panel)?.focus?.();
    this.panel?.setAttribute("aria-hidden", "false");
    if (!Shopify?.designMode) lockBodyScroll();
  }

  close() {
    this.#setOpen(false);
    this.panel?.setAttribute("aria-hidden", "true");
    if (!Shopify?.designMode) unlockBodyScroll();
  }

  onKeydown(e) {
    if (e.key === "Escape") {
      this.close();
    }
  }

  async onRemoveClick(e) {
    const btn = e.target.closest(SELECTORS.remove);
    if (!btn) return;
    e.preventDefault();

    const key = btn.dataset.lineKey;
    if (!key || btn.dataset.removing === "true") return;

    btn.dataset.removing = "true";
    btn.setAttribute("aria-busy", "true");
    btn.style.pointerEvents = "none";
    try {
      const cart = await this.#removeLineItem(key);
      dispatchCartEvent(CART_EVENTS.ITEM_REMOVED, { cart });
      // UPDATED triggers refreshCartDrawer(): the line disappears and the drawer
      // stays open on the current page — no redirect.
      dispatchCartEvent(CART_EVENTS.UPDATED, { cart });
    } catch (error) {
      console.error("[CartDrawer] remove failed", error);
      btn.dataset.removing = "false";
      btn.removeAttribute("aria-busy");
      btn.style.pointerEvents = "";
    }
  }

  /**
   * Remove a line item plus any license items attached to it
   * (properties._source_product_id === the item's product_id) in one atomic
   * /cart/update request. Returns the new cart state.
   *
   * Kept here rather than in cart-controller on purpose: cart-controller is only
   * ever pulled in via a relative ES import, whose canonical CDN URL is served
   * immutable (max-age 1y) and does not pick up new methods after a deploy.
   * cart-drawer.js loads through a versioned ?v= URL, so this ships reliably.
   */
  async #removeLineItem(key) {
    const state = await (
      await fetch(`${window.ThemeRoutes.cart}.js`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
    ).json();

    const target = state.items?.find((item) => item.key === key);
    const updates = { [key]: 0 };
    if (target) {
      state.items.forEach((item) => {
        const sourceId = item.properties?.["_source_product_id"];
        if (sourceId && String(sourceId) === String(target.product_id)) {
          updates[item.key] = 0;
        }
      });
    }

    const response = await fetch(`${window.ThemeRoutes.cartUpdate}.js`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error(`Cart remove failed (${response.status})`);

    // Custom tracking event: /cart/update.js is NOT instrumented by Shopify's
    // web pixel, so the standard product_removed_from_cart never fires here. We
    // publish our own event instead (subscribed to in the custom web pixel).
    // Only the main line is reported; attached license items are intentionally
    // excluded to mirror add_to_cart.
    if (target && window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish("webnetz_remove_from_cart", {
        currency: state.currency,
        value: (target.final_line_price || 0) / 100,
        items: [
          {
            item_id: String(target.product_id),
            item_name: target.product_title,
            item_variant: target.variant_title,
            item_brand: target.vendor,
            item_category: target.product_type,
            price: (target.final_price || 0) / 100,
            quantity: target.quantity,
          },
        ],
      });
    }

    return response.json();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    this.#callbackFunctions.forEach((fn) => fn());
    this.#callbackFunctions = [];
    this.overlay?.removeEventListener("click", this.close);
    this.closeBtn?.removeEventListener("click", this.close);
    this.removeEventListener("click", this.onRemoveClick);
    document.removeEventListener("keydown", this.onKeydown);
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}
